/**
 * Credits Service — connects to billing-service (port 5118) credit wallet
 */

import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import apiClient from '@/lib/api/client'
import type { CreditBalance, CreditTransaction, CreditPackage, AutoReloadConfig, CreditType } from '@/types/admin'

const BASE_URL = import.meta.env.VITE_BILLING_SERVICE_URL || 'https://billing.sgrc.api.i2tdigital.com'

export const creditKeys = {
  all: ['credits'] as const,
  balance: () => [...creditKeys.all, 'balance'] as const,
  transactions: (params?: Record<string, unknown>) => [...creditKeys.all, 'transactions', params] as const,
  packages: (creditType?: string) => [...creditKeys.all, 'packages', creditType] as const,
  usage: () => [...creditKeys.all, 'usage'] as const,
}

/**
 * Invalidate every credit query (balance, transactions, usage, …) so the
 * top-bar chip, AiCreditBadge and credit pages all refresh after an AI action
 * or a purchase/transfer. Call from mutation `onSuccess` or the AI-run wrapper.
 */
export function invalidateCredits(qc: QueryClient): void {
  qc.invalidateQueries({ queryKey: creditKeys.all })
}

export function useCreditBalance() {
  return useQuery<CreditBalance>({
    queryKey: creditKeys.balance(),
    queryFn: async () => {
      const { data } = await apiClient.get(`${BASE_URL}/api/v1/credits/balance`)
      return data
    },
  })
}

export function useCreditTransactions(params?: {
  credit_type?: CreditType
  operation?: 'credit' | 'debit'
  page?: number
  page_size?: number
}) {
  const qs = new URLSearchParams()
  if (params?.credit_type) qs.set('credit_type', params.credit_type)
  if (params?.operation) qs.set('operation', params.operation)
  if (params?.page) qs.set('page', String(params.page))
  if (params?.page_size) qs.set('page_size', String(params.page_size))

  return useQuery<{ items: CreditTransaction[]; total: number }>({
    queryKey: creditKeys.transactions(params),
    queryFn: async () => {
      const { data } = await apiClient.get(`${BASE_URL}/api/v1/credits/transactions?${qs}`)
      return data
    },
  })
}

export function useCreditPackages(creditType?: CreditType) {
  const qs = creditType ? `?credit_type=${creditType}` : ''
  return useQuery<CreditPackage[]>({
    queryKey: creditKeys.packages(creditType),
    queryFn: async () => {
      const { data } = await apiClient.get(`${BASE_URL}/api/v1/credits/packages${qs}`)
      return data?.items ?? data ?? []
    },
    staleTime: 10 * 60_000,
  })
}

export function useCreditUsageSummary() {
  return useQuery({
    queryKey: creditKeys.usage(),
    queryFn: async () => {
      const { data } = await apiClient.get(`${BASE_URL}/api/v1/credits/usage-summary`)
      return data
    },
  })
}

export function usePurchaseCredits() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: { package_id: string; quantity?: number; promo_code?: string }) => {
      const { data } = await apiClient.post(`${BASE_URL}/api/v1/credits/purchase`, payload)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: creditKeys.all }),
  })
}

export function useTransferCredits() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: { recipient_user_id: string; credit_type: CreditType; amount: number; note?: string }) => {
      const { data } = await apiClient.post(`${BASE_URL}/api/v1/credits/transfer`, payload)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: creditKeys.all }),
  })
}

export function useUpdateAutoReload() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (config: AutoReloadConfig) => {
      const { data } = await apiClient.patch(`${BASE_URL}/api/v1/credits/auto-reload`, config)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: creditKeys.balance() }),
  })
}

// ── Usage analytics + enterprise allocation (FR-CREDIT) ──────────────────────
export type HistoryKind = 'tous' | 'consommation' | 'achat' | 'transfert'

export function useCreditUsageMonthly(month?: string, creditType = 'ai') {
  return useQuery({
    queryKey: [...creditKeys.all, 'monthly', month ?? 'current', creditType],
    queryFn: async () => {
      const qs = new URLSearchParams({ credit_type: creditType })
      if (month) qs.set('month', month)
      const { data } = await apiClient.get(`${BASE_URL}/api/v1/credits/usage-monthly?${qs}`)
      return data
    },
    staleTime: 30_000,
  })
}

export function useCreditUsageHistory(filters: {
  kind?: HistoryKind; feature?: string; model?: string; page?: number; page_size?: number; credit_type?: string
}) {
  const { kind = 'tous', feature, model, page = 1, page_size = 10, credit_type = 'ai' } = filters
  return useQuery({
    queryKey: [...creditKeys.all, 'usage-history', { kind, feature, model, page, page_size, credit_type }],
    queryFn: async () => {
      const qs = new URLSearchParams({ credit_type, page: String(page), page_size: String(page_size) })
      if (kind !== 'tous') qs.set('kind', kind)
      if (feature) qs.set('feature', feature)
      if (model) qs.set('model', model)
      const { data } = await apiClient.get(`${BASE_URL}/api/v1/credits/usage-history?${qs}`)
      return data
    },
    staleTime: 10_000,
  })
}

export function useAllocateCredits() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: { recipient_user_ids: string[]; credit_type: CreditType; amount_each: number; note?: string }) => {
      const { data } = await apiClient.post(`${BASE_URL}/api/v1/credits/allocate`, payload)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: creditKeys.all }),
  })
}

/** Stripe V2 checkout for a credit package. Returns {checkout_url} (redirect) or
 * {mode:'dev', credits_added} when Stripe isn't configured. `promo_code` is
 * forwarded so the backend applies the real coupon discount/bonus authoritatively. */
export function useCheckoutCredits() {
  return useMutation({
    mutationFn: async (payload: { package_id: string; quantity?: number; success_url?: string; cancel_url?: string; promo_code?: string }) => {
      const { data } = await apiClient.post(`${BASE_URL}/api/v1/credits/checkout`, payload)
      return data as { mode: 'stripe' | 'dev'; status: string; checkout_url: string | null; credits_added?: number; session_id?: string }
    },
  })
}

// ── Coupon / promo validation ────────────────────────────────────────────────
// Real backend validation via billing-service coupons router
// (POST /api/v1/billing/coupons/validate). Passing `amount_cents` returns the
// concrete `discount_cents` for the current package; passing `base_credits`
// returns the concrete `bonus_credits`. Replaces the previous hardcoded -10%.
export interface CouponValidation {
  valid: boolean
  error?: string
  code?: string
  discount_type?: 'PERCENTAGE' | 'FIXED_AMOUNT' | 'FREE_MONTHS' | string
  discount_value?: number
  duration?: string
  discount_cents?: number
  bonus_credits?: number
}

export function useValidateCoupon() {
  return useMutation({
    mutationFn: async (payload: { code: string; amount_cents?: number; base_credits?: number; plan_id?: string }) => {
      const { data } = await apiClient.post(`${BASE_URL}/api/v1/billing/coupons/validate`, payload)
      return data as CouponValidation
    },
  })
}

export function useConfirmCheckout() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (session_id: string) => {
      const { data } = await apiClient.post(`${BASE_URL}/api/v1/credits/confirm-checkout`, { session_id })
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: creditKeys.all }),
  })
}
